<template>
  <section class="strategy-detail" aria-labelledby="strategy-detail-title">
    <button class="strategy-detail__back" type="button" @click="$emit('back')">
      <i class="el-icon-arrow-left" aria-hidden="true" />
      返回机器人
    </button>

    <div class="strategy-detail__heading">
      <div class="strategy-detail__identity">
        <div class="strategy-detail__title-row">
          <h1 id="strategy-detail-title">{{ title }}</h1>
          <span class="strategy-detail__tag">{{ robot.automation }} · {{ robot.name }}</span>
          <span class="strategy-detail__risk" :class="riskClass">{{ riskText }}</span>
          <span class="strategy-detail__status" :class="statusClass">
            <i aria-hidden="true" />
            {{ statusText }}
          </span>
        </div>
        <p>
          {{ robot.symbol }} · {{ robot.exchange }}
          <template v-if="createdAt">· 创建于 {{ createdAt }}</template>
          <template v-if="runningTime">· 已运行 {{ runningTime }}</template>
        </p>
      </div>

      <div class="strategy-detail__actions">
        <button
          v-if="capabilities.modify"
          class="detail-button"
          type="button"
          @click="emitAction('modify')"
        >
          修改
        </button>
        <button
          v-if="capabilities.pauseResume && robot.status === 'running'"
          class="detail-button"
          type="button"
          @click="emitAction('pause')"
        >
          暂停
        </button>
        <button
          v-else-if="capabilities.pauseResume && robot.status === 'paused'"
          class="detail-button detail-button--resume"
          type="button"
          @click="emitAction('resume')"
        >
          恢复运行
        </button>
        <button
          v-if="capabilities.delete"
          class="detail-button detail-button--danger"
          type="button"
          @click="emitAction('delete')"
        >
          删除
        </button>
        <button
          class="detail-button detail-button--icon"
          type="button"
          :disabled="loading"
          title="刷新详情"
          @click="loadDetail"
        >
          <i class="el-icon-refresh" :class="{ 'is-spinning': loading }" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div v-if="errors.length" class="strategy-detail__notice" role="status">
      <i class="el-icon-warning-outline" aria-hidden="true" />
      <span>{{ errors[0] }}</span>
      <button type="button" @click="loadDetail">重新读取</button>
    </div>

    <div class="strategy-metrics" :aria-busy="loading ? 'true' : 'false'">
      <div v-for="metric in metrics" :key="metric.label" class="strategy-metric">
        <span>{{ metric.label }}</span>
        <strong :class="metric.tone">{{ metric.value }}</strong>
        <div v-if="metric.progress !== null" class="strategy-metric__bar">
          <i :style="{ width: `${metric.progress}%` }" />
        </div>
      </div>
    </div>

    <div
      v-if="supportsProfitChart || supportsPositions"
      class="strategy-detail__main-grid"
      :class="{ 'is-single-column': !supportsProfitChart || !supportsPositions }"
    >
      <article v-if="supportsProfitChart" class="detail-card profit-chart-card">
        <header class="detail-card__header">
          <div>
            <h2>收益曲线</h2>
            <p>{{ profitMonthLabel }}收益</p>
          </div>
          <label class="month-filter">
            <span>月份</span>
            <el-date-picker
              v-model="profitMonth"
              class="month-filter__picker"
              type="month"
              format="yyyy年MM月"
              value-format="yyyy-MM"
              :editable="false"
              :clearable="false"
              @change="handleMonthChange"
            />
          </label>
        </header>
        <div class="profit-chart-card__numbers">
          <div>
            <span>本月收益 ({{ profitUnit }})</span>
            <strong :class="numberTone(monthProfit)">{{ formatSigned(monthProfit) }}</strong>
          </div>
          <div>
            <span>本月收益率</span>
            <strong :class="numberTone(monthProfitRate)">{{ formatRate(monthProfitRate) }}</strong>
          </div>
        </div>
        <div class="profit-chart-card__canvas">
          <EChartLine
            v-if="chartPoints.length"
            :points="chartPoints"
            :labels="chartLabels"
            color="#0b9b57"
            :area-opacity="0.18"
            :show-axis="true"
            aria-label="当前策略收益曲线"
          />
          <div v-else class="detail-empty detail-empty--chart">
            <i class="el-icon-data-line" aria-hidden="true" />
            <span>{{ loading ? '正在读取收益数据…' : '该策略暂无收益曲线' }}</span>
          </div>
        </div>
      </article>

      <article v-if="supportsPositions" class="detail-card position-card">
        <header class="detail-card__header">
          <div>
            <h2>{{ positionTitle }}</h2>
            <p>共 {{ positionTotal }} 条</p>
          </div>
          <div v-if="supportsPositionHistory" class="detail-card__tabs">
            <button
              type="button"
              :class="{ 'is-active': positionState === 0 }"
              @click="changePositionState(0)"
            >
              当前仓位
            </button>
            <button
              type="button"
              :class="{ 'is-active': positionState === 1 }"
              @click="changePositionState(1)"
            >
              历史仓位
            </button>
          </div>
          <span v-else class="live-sync">
            <i aria-hidden="true" />
            实时同步
          </span>
        </header>
        <div v-if="showPositionDateFilter" class="history-filter">
          <button
            v-for="option in datePresetOptions"
            :key="option.value"
            type="button"
            :class="{ 'is-active': positionDatePreset === option.value }"
            @click="changePositionDatePreset(option.value)"
          >
            {{ option.label }}
          </button>
          <div v-if="positionDatePreset === 'custom'" class="history-filter__dates">
            <input
              v-model="positionStartDate"
              type="date"
              @click="openDatePicker"
              @change="handlePositionDateChange('start')"
            >
            <span>至</span>
            <input
              v-model="positionEndDate"
              type="date"
              @click="openDatePicker"
              @change="handlePositionDateChange('end')"
            >
          </div>
        </div>
        <div v-if="positions.length" class="position-card__list">
          <div
            v-for="(position, index) in positions"
            :key="positionKey(position, index)"
            class="position-row"
          >
            <div class="position-row__top">
              <div class="position-row__identity">
                <strong>{{ positionSymbol(position) }}</strong>
                <span class="side-tag" :class="sideClass(position)">
                  {{ sideText(position) }}
                </span>
                <span v-if="positionLeverage(position)" class="position-row__leverage">
                  全仓 · {{ positionLeverage(position) }}X
                </span>
              </div>
              <strong
                v-if="!isHistoricalPosition"
                :class="numberTone(positionProfit(position))"
              >
                {{ formatSigned(positionProfit(position)) }}
                <small v-if="positionProfit(position) !== null">{{ profitUnit }}</small>
                <small v-if="positionRate(position) !== null">
                  （{{ formatRate(positionRate(position)) }}）
                </small>
              </strong>
              <strong v-else class="position-history-status">
                {{ historicalPositionStatus(position) }}
              </strong>
            </div>
            <dl>
              <div
                v-for="field in positionFields(position)"
                :key="field.label"
              >
                <dt>{{ field.label }}</dt>
                <dd :class="field.tone">{{ field.value }}</dd>
              </div>
            </dl>
          </div>
        </div>
        <div v-else class="detail-empty">
          <i class="el-icon-s-data" aria-hidden="true" />
          <span>{{ loading ? '正在读取仓位…' : `暂无${positionTitle}` }}</span>
        </div>
        <div v-if="showFloatingPnl" class="position-card__pnl">
          <span>当前浮动盈亏</span>
          <strong :class="numberTone(floatingPnl)">
            {{ formatSigned(floatingPnl) }} {{ profitUnit }}
          </strong>
        </div>
        <div
          v-if="supportsPositionPagination && positionTotal > positionPageSize"
          class="order-card__pagination"
        >
          <el-pagination
            background
            layout="prev, pager, next"
            :page-size="positionPageSize"
            :current-page="positionPage"
            :total="positionTotal"
            @current-change="changePositionPage"
          />
        </div>
        <p class="position-card__foot">仓位数据来自当前交易所；暂停策略不会自动平仓。</p>
      </article>
    </div>

    <article v-if="productType === 'martin'" class="detail-card martin-tools">
      <header class="detail-card__header">
        <div>
          <h2>马丁策略控制</h2>
          <p>管理补仓、止盈与循环操作</p>
        </div>
      </header>
      <div class="martin-tools__body">
        <div class="martin-shortcuts">
          <button
            v-for="shortcut in martinShortcuts"
            :key="shortcut.action"
            type="button"
            :class="{ 'is-active': shortcut.active }"
            @click="openMartinShortcut(shortcut)"
          >
            <span>{{ shortcut.label }}</span>
            <i aria-hidden="true" />
          </button>
        </div>
        <div class="martin-operations">
          <button type="button" @click="openReplenishmentControl">
            {{ replenishmentStopped ? '开启补单' : '停止补单' }}
          </button>
          <button type="button" @click="openOneClickReplenishment">一键补仓</button>
          <button class="is-danger" type="button" @click="openClearance">清仓卖出</button>
        </div>
      </div>
    </article>

    <article v-if="supportsPendingOrders" class="detail-card pending-order-card">
      <header class="detail-card__header">
        <div>
          <h2>当前挂单</h2>
          <p>共 {{ pendingOrderTotal }} 条 · 来自交易所实时委托</p>
        </div>
        <span class="live-sync">
          <i aria-hidden="true" />
          实时同步
        </span>
      </header>
      <div v-if="pendingOrders.length" class="pending-order-card__grid">
        <div
          v-for="(order, index) in pendingOrders"
          :key="positionKey(order, index)"
          class="position-row"
        >
          <div class="position-row__top">
            <div class="position-row__identity">
              <strong>{{ positionSymbol(order) }}</strong>
              <span class="side-tag" :class="sideClass(order)">{{ sideText(order) }}</span>
            </div>
          </div>
          <dl>
            <div v-for="field in pendingOrderFields(order)" :key="field.label">
              <dt>{{ field.label }}</dt>
              <dd>{{ field.value }}</dd>
            </div>
          </dl>
        </div>
      </div>
      <div v-else class="detail-empty">
        <i class="el-icon-s-order" aria-hidden="true" />
        <span>{{ loading ? '正在读取当前挂单…' : '当前没有挂单' }}</span>
      </div>
    </article>

    <article class="detail-card order-card">
      <header class="detail-card__header order-card__header">
        <div>
          <h2>{{ orderTitle }}</h2>
          <p>共 {{ orderTotal }} 条 · 按返回顺序展示</p>
        </div>
        <div class="order-card__filters">
          <button
            v-for="option in orderFilterOptions"
            :key="option.value"
            type="button"
            :class="{ 'is-active': orderState === option.value }"
            @click="changeOrderState(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </header>
      <div v-if="showOrderDateFilter" class="history-filter history-filter--orders">
        <button
          v-for="option in datePresetOptions"
          :key="option.value"
          type="button"
          :class="{ 'is-active': orderDatePreset === option.value }"
          @click="changeOrderDatePreset(option.value)"
        >
          {{ option.label }}
        </button>
        <div v-if="orderDatePreset === 'custom'" class="history-filter__dates">
          <input
            v-model="orderStartDate"
            type="date"
            @click="openDatePicker"
            @change="handleOrderDateChange('start')"
          >
          <span>至</span>
          <input
            v-model="orderEndDate"
            type="date"
            @click="openDatePicker"
            @change="handleOrderDateChange('end')"
          >
        </div>
      </div>

      <div class="order-table" role="table" :aria-label="orderTitle">
        <div
          class="order-table__row order-table__head"
          :class="{ 'has-detail': hasOrderDetail }"
          role="row"
        >
          <span role="columnheader">时间</span>
          <span role="columnheader">方向</span>
          <span role="columnheader">成交/委托价格</span>
          <span role="columnheader">数量</span>
          <span role="columnheader">已实现盈亏</span>
          <span role="columnheader">状态</span>
          <span v-if="hasOrderDetail" role="columnheader" />
        </div>
        <template v-if="orders.length">
          <div
            v-for="(order, index) in orders"
            :key="orderKey(order, index)"
            class="order-table__row"
            :class="{ 'has-detail': hasOrderDetail }"
            role="row"
          >
            <span role="cell">{{ orderTime(order) }}</span>
            <span role="cell">
              <i class="side-tag" :class="sideClass(order)">{{ sideText(order) }}</i>
            </span>
            <strong role="cell">{{ formatValue(rowNumber(order, orderPriceKeys), 8) }}</strong>
            <span role="cell">{{ formatValue(rowNumber(order, quantityKeys), 8) }}</span>
            <strong role="cell" :class="numberTone(orderProfit(order))">
              {{ formatSigned(orderProfit(order)) }}
            </strong>
            <span role="cell">
              <i class="status-tag" :class="orderStatusClass(order)">
                {{ orderStatusText(order) }}
              </i>
            </span>
            <span v-if="hasOrderDetail" role="cell" class="order-table__action">
              <button type="button" @click="openOrderDetail(order)">查看</button>
            </span>
          </div>
        </template>
        <div v-else class="detail-empty detail-empty--table">
          <i class="el-icon-document" aria-hidden="true" />
          <span>{{ loading ? '正在读取记录…' : '当前没有可展示的记录' }}</span>
        </div>
      </div>
      <div v-if="orderTotal > pageSize" class="order-card__pagination">
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :current-page="page"
          :total="orderTotal"
          @current-change="changePage"
        />
      </div>
    </article>

    <article class="detail-card strategy-parameters">
      <header class="detail-card__header">
        <div>
          <h2>策略参数</h2>
          <p>当前策略配置</p>
        </div>
      </header>
      <dl v-if="parameterRows.length" class="strategy-parameters__grid">
        <div v-for="row in parameterRows" :key="row.label">
          <dt>{{ row.label }}</dt>
          <dd>{{ row.value }}</dd>
        </div>
      </dl>
      <div v-else class="detail-empty detail-empty--compact">
        <span>当前没有可展示的策略参数</span>
      </div>
    </article>

    <transition name="detail-modal">
      <div
        v-if="quickDialog.visible"
        class="detail-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="quickDialog.title"
        @click.self="closeQuickDialog"
      >
        <div class="detail-modal__card">
          <button class="detail-modal__close" type="button" @click="closeQuickDialog">
            <i class="el-icon-close" aria-hidden="true" />
          </button>
          <span class="detail-modal__icon" :class="{ 'is-danger': quickDialog.danger }">
            <i :class="quickDialog.danger ? 'el-icon-warning-outline' : 'el-icon-set-up'" aria-hidden="true" />
          </span>
          <h3>{{ quickDialog.title }}</h3>
          <p>{{ quickDialog.message }}</p>

          <div v-if="quickDialog.command === 'one-click'" class="replenishment-form">
            <label>
              <span>补仓金额 ({{ quickDialog.quoteCoin || profitUnit }})</span>
              <input
                v-model.trim="quickDialog.amount"
                inputmode="decimal"
                placeholder="请输入补仓金额"
              >
              <small v-if="quickDialog.balance !== null">
                可用余额 {{ formatValue(quickDialog.balance, 8) }}
              </small>
            </label>
            <label>
              <span>成交价格</span>
              <input
                v-model.trim="quickDialog.price"
                inputmode="decimal"
                :placeholder="quickDialog.marketPrice === null ? '请输入价格' : `当前行情 ${quickDialog.marketPrice}`"
              >
              <small v-if="quickDialog.minimum !== null">
                最低补仓金额 {{ formatValue(quickDialog.minimum, 8) }}
              </small>
            </label>
            <label class="replenishment-form__check">
              <input v-model="quickDialog.stopAfter" type="checkbox">
              <span>本次补仓后停止继续补仓</span>
            </label>
          </div>
          <label
            v-else-if="
              (quickDialog.command === 'replenishment' && quickDialog.action === 0) ||
              quickDialog.command === 'clearance'
            "
            class="detail-modal__check"
          >
            <input v-model="quickDialog.stopAfter" type="checkbox">
            <span>操作完成后同时停止策略</span>
          </label>

          <div class="detail-modal__actions">
            <button type="button" @click="closeQuickDialog">取消</button>
            <button
              type="button"
              :class="{ 'is-danger': quickDialog.danger }"
              :disabled="quickSubmitting"
              @click="submitQuickDialog"
            >
              {{ quickSubmitting ? '提交中…' : '确认' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="detail-modal">
      <div
        v-if="orderDetailVisible"
        class="detail-modal"
        role="dialog"
        aria-modal="true"
        aria-label="成交明细"
        @click.self="orderDetailVisible = false"
      >
        <div class="detail-modal__card detail-modal__card--wide">
          <button class="detail-modal__close" type="button" @click="orderDetailVisible = false">
            <i class="el-icon-close" aria-hidden="true" />
          </button>
          <h3>成交明细</h3>
          <p>该循环或网格订单的成交记录</p>
          <div v-if="orderDetailLoading" class="detail-empty">正在读取成交明细…</div>
          <div v-else-if="orderDetails.length" class="order-detail-list">
            <div v-for="(item, index) in orderDetails" :key="orderKey(item, index)">
              <span class="side-tag" :class="sideClass(item)">{{ sideText(item) }}</span>
              <dl>
                <div><dt>时间</dt><dd>{{ orderTime(item) }}</dd></div>
                <div><dt>成交价格</dt><dd>{{ formatValue(rowNumber(item, orderPriceKeys), 8) }}</dd></div>
                <div><dt>数量</dt><dd>{{ formatValue(rowNumber(item, quantityKeys), 8) }}</dd></div>
                <div><dt>盈亏</dt><dd :class="numberTone(orderProfit(item))">{{ formatSigned(orderProfit(item)) }}</dd></div>
              </dl>
            </div>
          </div>
          <div v-else class="detail-empty">暂无成交明细</div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import { DatePicker } from 'element-ui'
import EChartLine from '@/components/rebuild/EChartLine.vue'
import { languageIntlLocale } from '@/utils/locale'
import {
  executeStrategyDetailCommand,
  loadMartinReplenishmentContext,
  loadStrategyDetail,
  loadStrategyOrderDetail
} from '@/api/strategy-detail'
import {
  robotActionCapabilities,
  robotProductType,
  robotTypeValue
} from '@/api/robot-actions'

const NUMBER_KEYS = {
  quantity: ['total_size', 'pos', 'size', 'quantity', 'qty', 'position', 'position_amount', 'order_size', 'amount'],
  margin: ['initial_margin', 'margin', 'position_margin', 'capital', 'space', 'cost', 'quote_volume', 'fund'],
  entry: ['avg_price', 'average_price', 'entry_price', 'open_price', 'price', 'avgPrice', 'avgPx'],
  mark: ['mark_price', 'market_price', 'last_price', 'current_price', 'markPrice', 'markPx'],
  orderPrice: ['deal_price', 'fill_price', 'avg_price', 'price', 'order_price', 'avgPx'],
  profit: ['profit', 'profit_total', 'profit_loss', 'pnl', 'realized_pnl', 'realizedPnl'],
  positionProfit: ['unrealized_pnl', 'unrealized_profit', 'upl', 'profit', 'pnl', 'profit_loss'],
  liquidation: ['force_price', 'liquidation_price', 'liq_price', 'liqPx'],
  marginRatio: ['margin_ratio', 'marginRate'],
  maintenanceMargin: ['mmr', 'maintenance_margin']
}

function emptyQuickDialog() {
  return {
    visible: false,
    title: '',
    message: '',
    command: '',
    action: 0,
    danger: false,
    amount: '',
    price: '',
    stopAfter: false,
    marketPrice: null,
    balance: null,
    minimum: null,
    quoteCoin: ''
  }
}

export default {
  name: 'StrategyDetailPanel',
  components: { EChartLine, ElDatePicker: DatePicker },
  props: {
    robot: { type: Object, required: true },
    robotInfo: { type: Object, default: null },
    allRobotInfos: { type: Array, default: () => [] }
  },
  data() {
    return {
      loading: false,
      requestId: 0,
      detail: {},
      positions: [],
      positionTotal: 0,
      pendingOrders: [],
      pendingOrderTotal: 0,
      orders: [],
      orderTotal: 0,
      chart: [],
      marketPrice: null,
      errors: [],
      page: 1,
      pageSize: 10,
      positionPage: 1,
      positionPageSize: 10,
      positionState: 0,
      orderState: 0,
      positionDatePreset: 'today',
      positionStartDate: '',
      positionEndDate: '',
      lastValidPositionDates: { start: '', end: '' },
      orderDatePreset: 'today',
      orderStartDate: '',
      orderEndDate: '',
      lastValidOrderDates: { start: '', end: '' },
      profitMonth: new Date().toISOString().slice(0, 7),
      quickDialog: emptyQuickDialog(),
      quickSubmitting: false,
      orderDetailVisible: false,
      orderDetailLoading: false,
      orderDetails: []
    }
  },
  computed: {
    productType() {
      return robotProductType(this.robot)
    },
    capabilities() {
      if (this.robot.raw && this.robot.raw.__historyStrategy) {
        return { modify: false, pauseResume: false, delete: false }
      }
      return robotActionCapabilities(this.robot)
    },
    title() {
      const kind = {
        astra: this.robot.name,
        futures_x: '永续合约',
        hedge: '永续合约',
        term: '期现交易',
        grid: '现货网格',
        martin: '现货马丁',
        bb: '币币交易',
        sj: '黄金三角',
        ub: '单币策略'
      }[this.productType] || this.robot.name
      const leverage = ['futures_x', 'hedge'].includes(this.productType)
        ? this.firstNumber(['leverage', 'lever'])
        : null
      return `${this.displaySymbol} · ${kind}${leverage === null ? '' : ` ${this.formatValue(leverage, 0)}X`}`
    },
    isMultiCoinComprehensive() {
      const raw = this.robot.raw || {}
      return (
        this.productType === 'futures_x' &&
        String(raw.futures_version || '').toLowerCase() === 'x_vip_astra'
      )
    },
    displaySymbol() {
      if (this.isMultiCoinComprehensive) {
        return `UPlus-${this.$tk('robot.multi_coin_comprehensive')}`
      }
      const text = String(this.robot.symbol || '--')
      if (!text.includes('/')) return text
      const parts = text.split('/').filter(Boolean)
      if (parts.length !== 2) return text
      const arrow = this.robot.positionDirection === 'both'
        ? '⇄'
        : this.robot.positionDirection === 'left' ? '←' : '→'
      return `${parts[0]} ${arrow} ${parts[1]}`
    },
    currentRobotInfo() {
      if (this.robotInfo) return this.robotInfo
      const targetType = robotTypeValue(this.robot)
      return this.allRobotInfos.find(item => Number(
        item && (
          item.robot_type !== undefined
            ? item.robot_type
            : item.robotType
        )
      ) === targetType) || null
    },
    robotPowerClosed() {
      return this.currentRobotInfo && Number(this.currentRobotInfo.state) === 2
    },
    statusText() {
      if (this.robotPowerClosed) return this.$t('已暂停 · 机器人关闭')
      if (this.robot.status === 'running') return this.$t('运行中')
      if (this.robot.status === 'paused') return this.$t('已暂停')
      return this.$t('状态未返回')
    },
    statusClass() {
      return this.robotPowerClosed || this.robot.status === 'paused'
        ? 'is-paused'
        : this.robot.status === 'running' ? 'is-running' : 'is-unknown'
    },
    riskText() {
      const health = this.health
      if (health === null) return this.$t('风险未返回')
      if (health < 60) return this.$t('高风险')
      if (health < 80) return this.$t('需关注')
      return this.$t('低风险')
    },
    riskClass() {
      if (this.health === null) return 'is-unknown'
      if (this.health < 60) return 'is-high'
      if (this.health < 80) return 'is-medium'
      return 'is-low'
    },
    createdAt() {
      return this.first(['add_time', 'create_time', 'created_at'], '')
    },
    runningTime() {
      const value = this.firstNumber(['run_time', 'running_days'])
      if (value !== null && value > 0) return this.$t(`${this.formatValue(value, 0)} 天`)
      return this.robot.days === null || this.robot.days === undefined
        ? ''
        : `${this.robot.days} 天`
    },
    profitUnit() {
      return this.robot.profitUnit || this.robot.investmentUnit || 'USDT'
    },
    investment() {
      const value = this.firstNumber([
        'space',
        'totalSpace',
        'total_space',
        'capital',
        'fund',
        'full_space',
        'after_space'
      ])
      return value === null ? this.robot.investmentValue : value
    },
    totalProfit() {
      const value = this.firstNumber([
        'profit_withdraw',
        'profit_total',
        'profit_total_all',
        'total_profit',
        'profit_loss',
        'profit'
      ])
      return value === null ? this.robot.profitValue : value
    },
    profitRate() {
      const value = this.firstNumber([
        'profit_rate',
        'profit_yield',
        'yield_rate',
        'rate'
      ])
      return value === null ? this.robot.rateValue : value
    },
    todayProfit() {
      return this.firstNumber([
        'today_profit',
        'profit_today',
        'today_income',
        'daily_profit'
      ])
    },
    floatingPnl() {
      return this.firstNumber([
        'floating_pnl',
        'pnl',
        'future_pnl',
        'unrealized_pnl'
      ])
    },
    health() {
      const value = this.firstNumber(['health', 'health_rate', 'margin_health'])
      return value === null ? this.toNumber(this.robot.health) : value
    },
    peakProfit() {
      const returned = this.firstNumber(['profit_peak', 'max_profit', 'peak_profit'])
      if (returned !== null) return returned
      if (!this.chart.length) return null
      return Math.max(...this.chart.map(item => Number(item.value)).filter(Number.isFinite))
    },
    metrics() {
      const rows = [
        {
          label: `投入金额 (${this.robot.investmentUnit || 'USDT'})`,
          value: this.formatValue(this.investment),
          tone: '',
          progress: null
        },
        {
          label: `总收益 (${this.profitUnit})`,
          value: this.formatSigned(this.totalProfit),
          tone: this.numberTone(this.totalProfit),
          progress: null
        },
        {
          label: '收益率',
          value: this.formatRate(this.profitRate),
          tone: this.numberTone(this.profitRate),
          progress: null
        },
        {
          label: `今日收益 (${this.profitUnit})`,
          value: this.formatSigned(this.todayProfit),
          tone: this.numberTone(this.todayProfit),
          progress: null
        },
        {
          label: '保证金健康度',
          value: this.health === null ? '--' : `${this.formatValue(this.health, 0)}%`,
          tone: this.health !== null && this.health < 60 ? 'negative' : this.health !== null ? 'positive' : '',
          progress: this.health === null ? null : Math.max(0, Math.min(100, this.health))
        },
        {
          label: `累计峰值 (${this.profitUnit})`,
          value: this.formatSigned(this.peakProfit),
          tone: this.numberTone(this.peakProfit),
          progress: null
        }
      ]
      if (this.productType === 'term') {
        rows.splice(3, 1, {
          label: `浮动盈亏 (${this.profitUnit})`,
          value: this.formatSigned(this.floatingPnl),
          tone: this.numberTone(this.floatingPnl),
          progress: null
        })
      }
      return rows
    },
    supportsProfitChart() {
      return ['astra', 'futures_x', 'hedge', 'sj', 'bb', 'ub'].includes(this.productType)
    },
    supportsPositions() {
      return ['astra', 'futures_x', 'hedge', 'term'].includes(this.productType)
    },
    supportsPendingOrders() {
      return this.productType === 'grid'
    },
    supportsPositionHistory() {
      return this.productType === 'hedge'
    },
    supportsPositionPagination() {
      return this.productType === 'hedge'
    },
    isHistoricalPosition() {
      return this.supportsPositionHistory && this.positionState === 1
    },
    showPositionDateFilter() {
      return this.isHistoricalPosition
    },
    showOrderDateFilter() {
      if (this.productType === 'hedge') return this.orderState === 1
      return ['sj', 'bb', 'ub'].includes(this.productType) && this.orderState !== 0
    },
    showFloatingPnl() {
      return this.productType === 'term'
    },
    datePresetOptions() {
      return [
        { label: '今天', value: 'today' },
        { label: '昨天', value: 'yesterday' },
        { label: '自定义', value: 'custom' }
      ]
    },
    chartPoints() {
      return this.chart.map(item => Number(item.value)).filter(Number.isFinite)
    },
    chartLabels() {
      return this.chart.map(item => item.date)
    },
    monthProfit() {
      if (!this.chart.length) return null
      const values = this.chart.map(item => this.toNumber(item.value)).filter(value => value !== null)
      return values.length ? values.reduce((sum, value) => sum + value, 0) : null
    },
    monthProfitRate() {
      if (!this.chart.length) return null
      const values = this.chart.map(item => this.toNumber(item.yield)).filter(value => value !== null)
      return values.length ? values.reduce((sum, value) => sum + value, 0) : null
    },
    profitMonthLabel() {
      const parts = String(this.profitMonth || '').split('-')
      return parts.length === 2 ? this.$t(`${parts[0]} 年 ${Number(parts[1])} 月`) : this.$t('当前月份')
    },
    positionTitle() {
      if (this.isHistoricalPosition) return this.$t('历史仓位')
      return this.$t('当前仓位')
    },
    isPendingOrders() {
      return ['grid', 'sj', 'bb', 'ub'].includes(this.productType)
    },
    orderTitle() {
      if (this.productType === 'martin') return this.$t('循环记录')
      if (this.productType === 'grid') return this.$t('成交记录')
      if (['sj', 'bb', 'ub'].includes(this.productType)) {
        return {
          0: '当前委托',
          1: '历史委托',
          2: '历史成交'
        }[this.orderState]
      }
      if (this.productType === 'hedge') {
        return this.orderState === 0 ? this.$t('当前委托') : this.$t('历史委托')
      }
      if (['astra', 'futures_x', 'term'].includes(this.productType)) return this.$t('当前委托')
      return this.$t('委托与成交记录')
    },
    hasOrderDetail() {
      return ['grid', 'martin'].includes(this.productType)
    },
    orderFilterOptions() {
      if (['sj', 'bb', 'ub'].includes(this.productType)) {
        return [
          { label: '当前委托', value: 0 },
          { label: '历史委托', value: 1 },
          { label: '历史成交', value: 2 }
        ]
      }
      if (this.productType === 'hedge') {
        return [
          { label: '当前委托', value: 0 },
          { label: '历史委托', value: 1 }
        ]
      }
      return []
    },
    quantityKeys() {
      return NUMBER_KEYS.quantity
    },
    marginKeys() {
      return NUMBER_KEYS.margin
    },
    entryPriceKeys() {
      return NUMBER_KEYS.entry
    },
    orderPriceKeys() {
      return NUMBER_KEYS.orderPrice
    },
    parameterRows() {
      const maxPosition = this.first(['maxPosition', 'max_position', 'max_used'], '')
      const definitions = [
        { label: '交易标的', value: this.first(['symbol', 'coin', 'instrument_id'], this.robot.symbol) },
        { label: '策略版本', value: this.versionText },
        { label: '杠杆倍数', value: this.withSuffix(this.firstNumber(['leverage', 'lever']), ' ×') },
        { label: '最大持仓', value: String(maxPosition) === '0' ? '无限大' : this.formatValue(maxPosition) },
        { label: '完成交易', value: this.withSuffix(this.firstNumber(['profit_times']), ' 单') },
        { label: '年化收益率', value: this.withSuffix(this.firstNumber(['profit_rate_year']), '%') },
        { label: '交易所余额', value: this.withCoin(this.firstNumber(['exchange_balance'])) },
        { label: '止损比例', value: this.withSuffix(this.firstNumber(['stop_loss', 'stop_ratio']), '%') },
        { label: '止盈比例', value: this.stopSurplusText },
        { label: '仓位比例', value: this.withSuffix(this.firstNumber(['position_proportion']), '%') },
        { label: '补亏损比例', value: this.withSuffix(this.firstNumber(['loss_transfer_rate']), '%') },
        { label: '已用资金', value: this.withCoin(this.firstNumber(['already_used'])) },
        { label: '网格数量', value: this.formatValue(this.firstNumber(['grid_number']), 0) },
        { label: '价格下限', value: this.formatValue(this.firstNumber(['min_price', 'lowest_price']), 8) },
        { label: '价格上限', value: this.formatValue(this.firstNumber(['max_price', 'highest_price']), 8) },
        { label: '首单金额', value: this.formatValue(this.firstNumber(['first_space', 'space']), 4) },
        { label: '最大补单次数', value: this.formatValue(this.firstNumber(['billing_quantity']), 0) },
        { label: '初始资产', value: this.autoInitialAssets },
        { label: '当前资产', value: this.autoCurrentAssets },
        { label: '策略模式', value: this.martinStrategyName }
      ]
      return definitions.filter(row => row.value !== '--' && row.value !== '')
    },
    stopSurplusText() {
      const value = this.firstNumber(['stop_surplus'])
      if (value === null) return '--'
      return value === 0 ? this.$t('盈利不设限') : `${this.formatValue(value * 100, 2)}%`
    },
    autoInitialAssets() {
      if (!['sj', 'bb', 'ub'].includes(this.productType)) return '--'
      return this.assetSummary({
        one: this.firstNumber(['space_one']),
        two: this.firstNumber(['space_two']),
        three: this.firstNumber(['space_three'])
      })
    },
    autoCurrentAssets() {
      if (!['sj', 'bb', 'ub'].includes(this.productType)) return '--'
      return this.assetSummary({
        one: this.firstNumber(['spaceAllOneNowNew']),
        two: this.firstNumber(['spaceAllTwoNowNew']),
        three: this.firstNumber(['space_three'])
      })
    },
    versionText() {
      const version = this.first(
        ['version_number', 'futures_version', 'version', 'old_new'],
        ''
      )
      if (version === '') return '--'
      if (String(version) === 'oneWay') return this.$t('单向')
      return String(version)
    },
    martinStrategyName() {
      if (this.productType !== 'martin') return '--'
      const type = this.firstNumber(['strategy_type'])
      return {
        0: '金字塔',
        1: '等距',
        2: '自定义'
      }[type] || (type === null ? '--' : `模式 ${type}`)
    },
    replenishmentStopped() {
      return Number(this.first(['stop_replenishment'], 1)) !== 1
    },
    martinShortcuts() {
      return [
        {
          action: 1,
          label: '满单止损',
          active: Number(this.first(['full_stop'], 0)) === 1
        },
        {
          action: 2,
          label: '追踪止盈',
          active: Number(this.first(['tracking_stop'], 0)) === 1
        },
        {
          action: 3,
          label: '追踪补仓',
          active: Number(this.first(['track_replenishment'], 0)) === 1
        }
      ]
    }
  },
  watch: {
    robot: {
      immediate: true,
      handler(next, previous) {
        if (!next) return
        if (!previous || next.id !== previous.id) {
          this.page = 1
          this.positionPage = 1
          this.positionState = 0
          this.orderState = 0
          this.positionDatePreset = 'today'
          this.positionStartDate = ''
          this.positionEndDate = ''
          this.lastValidPositionDates = { start: '', end: '' }
          this.orderDatePreset = 'today'
          this.orderStartDate = ''
          this.orderEndDate = ''
          this.lastValidOrderDates = { start: '', end: '' }
        }
        this.loadDetail()
      }
    }
  },
  methods: {
    async loadDetail(scope = 'all') {
      const requestScope = typeof scope === 'string' ? scope : 'all'
      const requestId = ++this.requestId
      this.loading = true
      try {
        const result = await loadStrategyDetail(this.robot, {
          only: requestScope,
          page: this.page,
          pageSize: this.pageSize,
          positionPage: this.positionPage,
          positionPageSize: this.positionPageSize,
          positionState: this.positionState,
          orderState: this.orderState,
          positionStartDate: this.positionStartDate,
          positionEndDate: this.positionEndDate,
          orderStartDate: this.orderStartDate,
          orderEndDate: this.orderEndDate,
          month: this.profitMonth
        })
        if (requestId !== this.requestId) return
        if (requestScope === 'all' || requestScope === 'detail') {
          this.detail = result.detail || {}
          this.marketPrice = result.marketPrice
        }
        if (requestScope === 'all' || requestScope === 'positions') {
          this.positions = result.positions || []
          this.positionTotal = result.positionTotal || 0
        }
        if (requestScope === 'all' || requestScope === 'pending') {
          this.pendingOrders = result.pendingOrders || []
          this.pendingOrderTotal = result.pendingOrderTotal || 0
        }
        if (requestScope === 'all' || requestScope === 'orders') {
          this.orders = result.orders || []
          this.orderTotal = result.orderTotal || 0
        }
        if (requestScope === 'all' || requestScope === 'chart') {
          this.chart = result.chart || []
        }
        this.errors = result.errors || []
      } catch (error) {
        if (requestId !== this.requestId) return
        this.errors = [error.message || this.$t('策略详情读取失败')]
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    emitAction(action) {
      this.$emit('action', { action, robot: this.robot })
    },
    emitToast(message, type = 'info') {
      this.$emit('toast', { message, type })
    },
    first(keys, fallback = '') {
      const sources = [this.detail, this.robot.raw || {}]
      for (const source of sources) {
        for (const key of keys) {
          const value = source && source[key]
          if (value !== '' && value !== null && value !== undefined) return value
        }
      }
      return fallback
    },
    firstNumber(keys) {
      return this.toNumber(this.first(keys, null))
    },
    rowNumber(row, keys) {
      for (const key of keys) {
        const value = this.toNumber(row && row[key])
        if (value !== null) return value
      }
      return null
    },
    toNumber(value) {
      if (value === '' || value === null || value === undefined) return null
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    formatValue(value, digits = 2) {
      const number = this.toNumber(value)
      if (number === null) return value === '' || value === null || value === undefined ? '--' : String(value)
      return number.toLocaleString(languageIntlLocale(), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      })
    },
    formatSigned(value, digits = 2) {
      const number = this.toNumber(value)
      if (number === null) return '--'
      return `${number > 0 ? '+' : ''}${this.formatValue(number, digits)}`
    },
    formatRate(value) {
      const number = this.toNumber(value)
      if (number === null) return '--'
      return `${number > 0 ? '+' : ''}${this.formatValue(number, 2)}%`
    },
    numberTone(value) {
      const number = this.toNumber(value)
      if (number === null || number === 0) return ''
      return number > 0 ? 'positive' : 'negative'
    },
    withSuffix(value, suffix) {
      const number = this.toNumber(value)
      return number === null ? '--' : `${this.formatValue(number, 2)}${suffix}`
    },
    withCoin(value) {
      const number = this.toNumber(value)
      return number === null ? '--' : `${this.formatValue(number, 4)} ${this.profitUnit}`
    },
    assetSummary(values) {
      const raw = this.robot.raw || {}
      const rows = [
        { coin: raw.coin_one, value: values.one },
        { coin: raw.coin_two, value: values.two },
        { coin: raw.coin_three, value: values.three }
      ].filter(item => item.coin && item.value !== null)
      return rows.length
        ? rows.map(item => `${item.coin} (${this.formatValue(item.value, 8)})`).join('、')
        : '--'
    },
    rowValue(row, keys, fallback = null) {
      for (const key of keys) {
        const value = row && row[key]
        if (value !== '' && value !== null && value !== undefined) return value
      }
      return fallback
    },
    positionFields(row) {
      const numberField = (label, keys, digits = 4, suffix = '', tone = '') => {
        const value = this.rowNumber(row, keys)
        return {
          label,
          value: value === null ? '--' : `${this.formatValue(value, digits)}${suffix}`,
          tone: tone || ''
        }
      }
      const textField = (label, keys) => ({
        label,
        value: String(this.rowValue(row, keys, '--')),
        tone: ''
      })

      if (this.isHistoricalPosition) {
        const profit = this.rowNumber(row, NUMBER_KEYS.profit)
        const rate = this.rowNumber(row, ['profit_rate'])
        return [
          numberField('平均开仓价', ['avg_price', 'entry_price'], 8),
          numberField('平均平仓价', ['close_price'], 8),
          numberField('平仓收益', NUMBER_KEYS.profit, 2, ` ${this.profitUnit}`, this.numberTone(profit)),
          numberField('收益率', ['profit_rate'], 2, '%', this.numberTone(rate)),
          numberField('最大持仓', ['max_open', 'max_position'], 8),
          numberField('已平仓数量', ['close_size', 'closed_size'], 8),
          textField('开仓时间', ['start_time', 'open_time', 'create_time']),
          textField('完成时间', ['close_time', 'finish_time', 'update_time'])
        ]
      }

      if (this.productType === 'term') {
        const upl = this.rowNumber(row, ['upl', 'unrealized_pnl', 'profit'])
        const quantity = this.rowNumber(row, ['pos', 'total_size', 'size'])
        return [
          {
            label: '持仓数量',
            value: quantity === null ? '--' : this.formatValue(Math.abs(quantity), 8),
            tone: ''
          },
          numberField('未实现盈亏', ['upl', 'unrealized_pnl', 'profit'], 2, ` ${this.profitUnit}`, this.numberTone(upl)),
          numberField('强平价格', NUMBER_KEYS.liquidation, 8),
          textField('持仓方向', ['posSide', 'side', 'direction'])
        ]
      }

      if (this.isPendingOrders) {
        return [
          numberField('委托数量', NUMBER_KEYS.quantity, 8),
          numberField('委托金额', NUMBER_KEYS.margin, 4),
          numberField('委托价格', NUMBER_KEYS.entry, 8),
          {
            label: '当前行情',
            value: this.formatValue(this.markPrice(row), 8),
            tone: ''
          }
        ]
      }

      return [
        numberField('持仓数量', NUMBER_KEYS.quantity, 8),
        numberField('保证金率', NUMBER_KEYS.marginRatio, 2, '%'),
        numberField('标记价格', NUMBER_KEYS.mark, 8),
        numberField('维持保证金', NUMBER_KEYS.maintenanceMargin, 4),
        numberField(`初始保证金 (${this.profitUnit})`, NUMBER_KEYS.margin, 4),
        numberField('开仓均价', NUMBER_KEYS.entry, 8),
        numberField('强平价格', NUMBER_KEYS.liquidation, 8),
        numberField(`保证金余额 (${this.profitUnit})`, ['margin', 'margin_balance'], 4)
      ]
    },
    historicalPositionStatus(row) {
      return Number(this.rowValue(row, ['status'], 1)) === 0 ? this.$t('部分平仓') : this.$t('全部平仓')
    },
    positionKey(row, index) {
      return row.id || row.order_id || row.client_order_id || `${index}-${row.side || ''}`
    },
    orderKey(row, index) {
      return row.id || row.order_id || row.buy_order_id || row.sid || `${index}-${this.orderTime(row)}`
    },
    sideClass(row) {
      const type = String(row && row.type || '').toLowerCase()
      if (type === 'close') {
        return String(row && row.side || '').toLowerCase() === 'sell' ? 'is-buy' : 'is-sell'
      }
      if (['buy', 'more', 'flat_empty'].includes(type)) return 'is-buy'
      if (['sell', 'empty', 'flat_more'].includes(type)) return 'is-sell'
      const side = String(
        row && (
          row.__pendingSide ||
          row.side ||
          row.direction ||
          row.posSide ||
          ''
        )
      ).toLowerCase()
      return ['buy', 'long', '1'].includes(side) ? 'is-buy' : 'is-sell'
    },
    sideText(row) {
      const type = String(row && row.type || '').toLowerCase()
      const sideValue = String(row && row.side || '').toLowerCase()
      if (type === 'open') return sideValue === 'buy' ? this.$t('开多') : this.$t('开空')
      if (type === 'close') return sideValue === 'buy' ? this.$t('平多') : this.$t('平空')
      const typeNames = {
        buy: '现货买入',
        sell: '现货卖出',
        empty: '合约开空',
        flat_empty: '合约平空',
        more: '合约开多',
        flat_more: '合约平多'
      }
      if (typeNames[type]) return typeNames[type]
      const side = String(
        row && (
          row.__pendingSide ||
          row.side ||
          row.direction ||
          row.posSide ||
          ''
        )
      ).toLowerCase()
      if (['buy', 'long', '1'].includes(side)) return this.isPendingOrders ? this.$t('买入') : this.$t('多仓')
      if (['sell', 'short', '2', '-1'].includes(side)) return this.isPendingOrders ? this.$t('卖出') : this.$t('空仓')
      return this.$t('方向未返回')
    },
    positionProfit(row) {
      return this.rowNumber(row, NUMBER_KEYS.positionProfit)
    },
    positionRate(row) {
      return this.rowNumber(row, ['profit_rate', 'yield_rate', 'rate'])
    },
    positionSymbol(row) {
      return String(this.rowValue(
        row,
        ['symbol', 'instrument_id', 'instId', 'coin'],
        this.robot.symbol || '--'
      ))
    },
    positionLeverage(row) {
      const value = this.rowNumber(row, ['leverage', 'lever'])
      if (value !== null) return this.formatValue(value, 0)
      const detailValue = this.firstNumber(['leverage', 'lever'])
      return detailValue === null ? '' : this.formatValue(detailValue, 0)
    },
    pendingOrderFields(row) {
      return [
        {
          label: '委托数量',
          value: this.formatValue(this.rowNumber(row, NUMBER_KEYS.quantity), 8)
        },
        {
          label: '委托价格',
          value: this.formatValue(this.rowNumber(row, NUMBER_KEYS.orderPrice), 8)
        },
        {
          label: '委托金额',
          value: this.formatValue(this.rowNumber(row, NUMBER_KEYS.margin), 4)
        },
        {
          label: '当前行情',
          value: this.formatValue(this.markPrice(row), 8)
        }
      ]
    },
    orderProfit(row) {
      return this.rowNumber(row, NUMBER_KEYS.profit)
    },
    markPrice(row) {
      const value = this.rowNumber(row, NUMBER_KEYS.mark)
      return value === null ? this.marketPrice : value
    },
    orderTime(row) {
      const keys = ['entrustTime', 'deal_time', 'create_time', 'update_time', 'add_time', 'time', 'timestamp']
      for (const key of keys) {
        const value = row && row[key]
        if (value !== '' && value !== null && value !== undefined) return String(value)
      }
      return '--'
    },
    orderStatusText(row) {
      const state = String(row && (row.state !== undefined ? row.state : row.status))
      if (state === '-1' || state.toLowerCase() === 'cancelled') return this.$t('已撤销')
      if (state === '0' || state.toLowerCase() === 'pending') return this.$t('待成交')
      if (state === '1' || state === '2' || state.toLowerCase() === 'filled') return this.$t('已成交')
      return state && state !== 'undefined' ? state : this.$t('状态未返回')
    },
    orderStatusClass(row) {
      const text = this.orderStatusText(row)
      if (text === this.$t('已撤销')) return 'is-cancelled'
      if (text === this.$t('待成交')) return 'is-pending'
      return 'is-filled'
    },
    changePage(page) {
      this.page = page
      this.loadDetail('orders')
    },
    changePositionPage(page) {
      this.positionPage = page
      this.loadDetail('positions')
    },
    changePositionState(state) {
      if (this.positionState === state) return
      this.positionState = state
      this.positionPage = 1
      if (state === 1) {
        this.applyDatePreset('position', 'today')
      } else {
        this.positionStartDate = ''
        this.positionEndDate = ''
        this.lastValidPositionDates = { start: '', end: '' }
      }
      this.loadDetail('positions')
    },
    changeOrderState(state) {
      if (this.orderState === state) return
      this.orderState = state
      this.page = 1
      if (state === 0) {
        this.orderStartDate = ''
        this.orderEndDate = ''
        this.lastValidOrderDates = { start: '', end: '' }
      } else {
        this.applyDatePreset('order', 'today')
      }
      this.loadDetail('orders')
    },
    formatCalendarDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    openDatePicker(event) {
      const input = event && event.currentTarget
      if (!input) return
      input.focus()
      if (typeof input.showPicker === 'function') {
        try {
          input.showPicker()
        } catch (error) {
          // Older browsers still open the native selector through the focused input.
        }
      }
    },
    applyDatePreset(kind, preset) {
      const date = new Date()
      if (preset === 'yesterday') date.setDate(date.getDate() - 1)
      const value = preset === 'custom' ? '' : this.formatCalendarDate(date)
      this[`${kind}DatePreset`] = preset
      if (preset !== 'custom') {
        this[`${kind}StartDate`] = value
        this[`${kind}EndDate`] = value
        if (kind === 'position') {
          this.lastValidPositionDates = { start: value, end: value }
        } else {
          this.lastValidOrderDates = { start: value, end: value }
        }
      }
    },
    changePositionDatePreset(preset) {
      this.applyDatePreset('position', preset)
      this.positionPage = 1
      if (preset !== 'custom') this.loadDetail('positions')
    },
    changeOrderDatePreset(preset) {
      this.applyDatePreset('order', preset)
      this.page = 1
      if (preset !== 'custom') this.loadDetail('orders')
    },
    validateDateRange(kind, changedField) {
      const startKey = `${kind}StartDate`
      const endKey = `${kind}EndDate`
      const cacheKey = kind === 'position'
        ? 'lastValidPositionDates'
        : 'lastValidOrderDates'
      const startDate = this[startKey]
      const endDate = this[endKey]
      if (!startDate || !endDate) return false
      if (startDate > endDate) {
        this.emitToast(this.$t('开始时间不能大于结束时间'), 'error')
        const previous = this[cacheKey] || { start: '', end: '' }
        this.$nextTick(() => {
          this[changedField === 'start' ? startKey : endKey] = previous[changedField] || ''
        })
        return false
      }
      this[cacheKey] = { start: startDate, end: endDate }
      return true
    },
    handlePositionDateChange(changedField) {
      if (!this.validateDateRange('position', changedField)) return
      this.positionPage = 1
      this.loadDetail('positions')
    },
    handleOrderDateChange(changedField) {
      if (!this.validateDateRange('order', changedField)) return
      this.page = 1
      this.loadDetail('orders')
    },
    handleMonthChange() {
      this.page = 1
      this.loadDetail('chart')
    },
    openMartinShortcut(shortcut) {
      this.quickDialog = {
        ...emptyQuickDialog(),
        visible: true,
        title: shortcut.active ? this.$t(`关闭${shortcut.label}`) : this.$t(`开启${shortcut.label}`),
        message: this.$t(`确认修改“${shortcut.label}”状态？`),
        command: 'shortcut',
        action: shortcut.action
      }
    },
    openReplenishmentControl() {
      this.quickDialog = {
        ...emptyQuickDialog(),
        visible: true,
        title: this.replenishmentStopped ? '开启补单' : '停止补单',
        message: this.replenishmentStopped
          ? '确认恢复当前马丁策略的自动补单？'
          : '确认本次循环停止继续补单？已有委托和持仓不会自动撤销。',
        command: 'replenishment',
        action: this.replenishmentStopped ? 1 : 0
      }
    },
    openClearance() {
      if (Number(this.first(['clearance_sale'], 1)) !== 1) {
        this.emitToast(this.$t('当前策略正在执行清仓操作，请稍候'), 'info')
        return
      }
      this.quickDialog = {
        ...emptyQuickDialog(),
        visible: true,
        title: this.$t('清仓卖出'),
        message: this.$t('确认清仓卖出本次循环的持仓？'),
        command: 'clearance',
        action: 0,
        danger: true
      }
    },
    async openOneClickReplenishment() {
      if (Number(this.first(['one_click_replenishment'], 1)) !== 1) {
        this.emitToast(this.$t('当前策略正在执行补仓操作，请稍候'), 'info')
        return
      }
      this.quickDialog = {
        ...emptyQuickDialog(),
        visible: true,
        title: this.$t('一键补仓'),
        message: this.$t('请确认补仓金额，提交后将按当前行情执行。')
      }
      try {
        const context = await loadMartinReplenishmentContext(this.robot, this.detail)
        const price = context.price
        const minimum = price !== null && context.minQuantity !== null
          ? Number((price * context.minQuantity * 1.5).toFixed(10))
          : null
        this.quickDialog = {
          ...this.quickDialog,
          command: 'one-click',
          marketPrice: price,
          price: price === null ? '' : String(price),
          balance: context.balance,
          minimum,
          quoteCoin: context.quoteCoin
        }
        if (context.errors.length) this.emitToast(context.errors[0], 'error')
      } catch (error) {
        this.closeQuickDialog()
        this.emitToast(error.message || this.$t('补仓校验数据读取失败'), 'error')
      }
    },
    closeQuickDialog() {
      if (this.quickSubmitting) return
      this.quickDialog = emptyQuickDialog()
    },
    async submitQuickDialog() {
      const dialog = this.quickDialog
      let amount = null
      let price = null
      if (dialog.command === 'one-click') {
        amount = this.toNumber(dialog.amount)
        price = this.toNumber(dialog.price)
        if (amount === null || amount <= 0) {
          this.emitToast(this.$t('请输入有效的补仓金额'), 'error')
          return
        }
        if (price === null || price <= 0) {
          this.emitToast(this.$t('请输入有效的成交价格'), 'error')
          return
        }
        if (dialog.minimum !== null && amount < dialog.minimum) {
          this.emitToast(this.$t(`补仓金额不能低于 ${this.formatValue(dialog.minimum, 8)}`), 'error')
          return
        }
        if (dialog.balance !== null && amount > dialog.balance) {
          this.emitToast(this.$t('补仓金额超过交易所可用余额'), 'error')
          return
        }
      }
      this.quickSubmitting = true
      try {
        await executeStrategyDetailCommand({
          robot: this.robot,
          command: dialog.command,
          action: dialog.command === 'one-click'
            ? (dialog.stopAfter ? 1 : 0)
            : (
              dialog.stopAfter &&
              ['replenishment', 'clearance'].includes(dialog.command)
                ? 2
                : dialog.action
            ),
          amount,
          price
        })
        this.quickDialog = emptyQuickDialog()
        await this.loadDetail()
        this.$emit('refresh')
        this.emitToast(this.$t('策略操作成功，详情已重新同步'), 'success')
      } catch (error) {
        this.emitToast(error.message || this.$t('策略操作失败'), 'error')
      } finally {
        this.quickSubmitting = false
      }
    },
    async openOrderDetail(order) {
      this.orderDetailVisible = true
      this.orderDetailLoading = true
      this.orderDetails = []
      try {
        this.orderDetails = await loadStrategyOrderDetail(this.robot, order)
      } catch (error) {
        this.emitToast(error.message || this.$t('成交明细读取失败'), 'error')
      } finally {
        this.orderDetailLoading = false
      }
    }
  }
}
</script>

<style scoped>
.strategy-detail {
  color: #1b1f2a;
  animation: detail-in .24s ease;
}

@keyframes detail-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.strategy-detail__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 15px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1f6bff;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}

.strategy-detail__back:hover {
  color: #1558db;
}

.strategy-detail__heading,
.strategy-detail__title-row,
.strategy-detail__actions,
.detail-card__header,
.martin-tools__body {
  display: flex;
  align-items: center;
}

.strategy-detail__heading {
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.strategy-detail__identity {
  min-width: 0;
}

.strategy-detail__title-row {
  flex-wrap: wrap;
  gap: 10px;
}

.strategy-detail__title-row h1 {
  margin: 0;
  font-size: 24px;
  line-height: 1.25;
  font-weight: 800;
  letter-spacing: .1px;
}

.strategy-detail__identity > p {
  margin: 7px 0 0;
  color: #8a90a0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.strategy-detail__tag,
.strategy-detail__risk,
.strategy-detail__status {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.strategy-detail__tag {
  padding: 4px 11px;
  border-radius: 6px;
  background: #ebf1fe;
  color: #1f6bff;
}

.strategy-detail__risk {
  padding: 4px 11px;
}

.strategy-detail__risk.is-low {
  color: #0b7a47;
  background: #e8f6ee;
}

.strategy-detail__risk.is-medium {
  color: #9a6700;
  background: #fff6dc;
}

.strategy-detail__risk.is-high {
  color: #c9363e;
  background: #fceae8;
}

.strategy-detail__risk.is-unknown {
  color: #737a89;
  background: #f0f2f6;
}

.strategy-detail__status {
  gap: 6px;
  padding: 4px 11px;
}

.strategy-detail__status i,
.live-sync i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.strategy-detail__status.is-running {
  color: #0b7a47;
  background: #e8f6ee;
}

.strategy-detail__status.is-paused {
  color: #72798a;
  background: #f0f2f6;
}

.strategy-detail__status.is-unknown {
  color: #9a6700;
  background: #fff6dc;
}

.strategy-detail__actions {
  flex-shrink: 0;
  gap: 10px;
}

.detail-button {
  min-height: 38px;
  padding: 8px 19px;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  background: #fff;
  color: #3a3f4e;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color .16s ease, color .16s ease, background .16s ease, transform .16s ease;
}

.detail-button:hover:not(:disabled) {
  border-color: #a9c4f7;
  color: #1f6bff;
  transform: translateY(-1px);
}

.detail-button--resume {
  border-color: #b8e6ca;
  background: #e8f6ee;
  color: #0b7a47;
}

.detail-button--danger {
  border-color: #f3d2cf;
  color: #e5484d;
}

.detail-button--danger:hover:not(:disabled) {
  border-color: #eeb3ae;
  background: #fceae8;
  color: #d83238;
}

.detail-button--icon {
  width: 38px;
  padding: 0;
}

.detail-button:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.is-spinning {
  animation: detail-spin .9s linear infinite;
}

@keyframes detail-spin {
  to { transform: rotate(360deg); }
}

.strategy-detail__notice {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: -5px 0 15px;
  padding: 10px 14px;
  border: 1px solid #f2d5a7;
  border-radius: 9px;
  background: #fffaf0;
  color: #875c10;
  font-size: 12.5px;
}

.strategy-detail__notice span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.strategy-detail__notice button {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #1f6bff;
  font-weight: 700;
  cursor: pointer;
}

.strategy-metrics {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin-bottom: 20px;
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 6px 20px rgba(16, 24, 40, .04);
  overflow: hidden;
}

.strategy-metric {
  min-width: 0;
  padding: 20px 22px;
}

.strategy-metric + .strategy-metric {
  border-left: 1px solid #f0f2f6;
}

.strategy-metric > span {
  display: block;
  overflow: hidden;
  color: #8a90a0;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.strategy-metric > strong {
  display: block;
  margin-top: 7px;
  overflow: hidden;
  font-size: 21px;
  line-height: 1.2;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.positive {
  color: #0b9b57 !important;
}

.negative {
  color: #e5484d !important;
}

.strategy-metric__bar {
  width: 110px;
  height: 5px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 99px;
  background: #eef0f5;
}

.strategy-metric__bar i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #0e9f5c;
}

.strategy-detail__main-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 20px;
  align-items: stretch;
}

.strategy-detail__main-grid.is-single-column {
  grid-template-columns: minmax(0, 1fr);
}

.detail-card {
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 6px 20px rgba(16, 24, 40, .04);
}

.detail-card__header {
  justify-content: space-between;
  gap: 16px;
  padding: 21px 22px 13px;
}

.detail-card__header h2 {
  margin: 0;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 800;
}

.detail-card__header p {
  margin: 4px 0 0;
  color: #98a0af;
  font-size: 12px;
}

.month-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #8a90a0;
  font-size: 12px;
}

.month-filter__picker {
  width: 160px;
}

.month-filter ::v-deep .el-input__inner {
  height: 34px;
  padding: 0 34px 0 12px;
  border: 1px solid #dfe3ec;
  border-radius: 7px;
  outline: none;
  color: #3a3f4e;
  background: #fff;
  cursor: pointer;
}

.month-filter ::v-deep .el-input__prefix {
  right: 8px;
  left: auto;
}

.month-filter ::v-deep .el-input__icon {
  line-height: 34px;
}

.month-filter ::v-deep .el-input.is-focus .el-input__inner {
  border-color: #8fb2f5;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, .09);
}

.profit-chart-card {
  min-height: 390px;
  padding-bottom: 12px;
}

.profit-chart-card__numbers {
  display: flex;
  gap: 38px;
  padding: 0 22px 4px;
}

.profit-chart-card__numbers span {
  display: block;
  color: #8a90a0;
  font-size: 12px;
}

.profit-chart-card__numbers strong {
  display: block;
  margin-top: 4px;
  font-size: 20px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.profit-chart-card__canvas {
  height: 255px;
  padding: 20px 22px 0;
}

.position-card {
  display: flex;
  min-height: 390px;
  flex-direction: column;
}

.live-sync {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #0b7a47;
  font-size: 12px;
  font-weight: 700;
}

.live-sync i {
  animation: detail-pulse 2s ease infinite;
}

.detail-card__tabs,
.history-filter {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: #f5f6f9;
}

.detail-card__tabs button,
.history-filter > button {
  padding: 6px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #7d8493;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.detail-card__tabs button.is-active,
.history-filter > button.is-active {
  color: #1f6bff;
  background: #fff;
  box-shadow: 0 1px 4px rgba(16, 24, 40, .08);
}

.history-filter {
  display: grid;
  width: calc(100% - 44px);
  grid-template-columns: repeat(3, max-content);
  box-sizing: border-box;
  margin: 0 22px 13px;
}

.history-filter--orders {
  margin-bottom: 16px;
}

.history-filter__dates {
  display: grid;
  width: 100%;
  max-width: 380px;
  grid-column: 1 / -1;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  box-sizing: border-box;
  padding: 5px 0 1px;
  color: #98a0af;
  font-size: 12px;
}

.history-filter__dates input {
  width: 100%;
  height: 30px;
  min-width: 0;
  box-sizing: border-box;
  padding: 0 7px;
  border: 1px solid #dfe3ec;
  border-radius: 6px;
  outline: 0;
  color: #495065;
  background: #fff;
  cursor: pointer;
}

.history-filter__dates input:focus {
  border-color: #8fb2f5;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, .08);
}

@keyframes detail-pulse {
  50% { opacity: .35; }
}

.position-card__list {
  display: flex;
  max-height: 290px;
  padding: 0 22px;
  overflow: auto;
  flex-direction: column;
  gap: 10px;
}

.position-row {
  padding: 13px 15px;
  border: 1px solid #eef1f7;
  border-radius: 12px;
  background: #fbfcfe;
}

.position-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.position-row__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.position-row__identity > strong {
  max-width: 160px;
  overflow: hidden;
  color: #2e3445;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.position-row__leverage {
  padding: 3px 7px;
  border-radius: 5px;
  color: #666f80;
  background: #eef1f6;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
}

.position-row__top > strong {
  color: #5a6072;
  font-size: 14px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.position-row__top small {
  color: #8a90a0;
  font-size: 10px;
}

.position-history-status {
  color: #7d8493 !important;
}

.side-tag {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.4;
  font-weight: 800;
  font-style: normal;
  white-space: nowrap;
}

.side-tag.is-buy {
  color: #0b7a47;
  background: #e8f6ee;
}

.side-tag.is-sell {
  color: #c9363e;
  background: #fceae8;
}

.position-row dl {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px 16px;
  margin: 11px 0 0;
}

.position-row dl div {
  display: flex;
  min-width: 0;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
}

.position-row dt {
  color: #8a90a0;
}

.position-row dd {
  margin: 0;
  overflow: hidden;
  color: #3a3f4e;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.position-row dd.positive {
  color: #0b9b57;
}

.position-row dd.negative {
  color: #e5484d;
}

.position-card__pnl {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 14px 22px 0;
  padding: 13px 15px;
  border: 1px solid #dcece4;
  border-radius: 10px;
  color: #526071;
  background: #f0faf5;
  font-size: 13px;
}

.position-card__pnl strong {
  color: #0b7a47;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.position-card__foot {
  margin: auto 22px 0;
  padding: 11px 0 15px;
  border-top: 1px solid #f3f4f8;
  color: #98a0af;
  font-size: 11.5px;
}

.pending-order-card {
  overflow: hidden;
}

.pending-order-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 0 22px 20px;
}

.detail-empty {
  display: flex;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  color: #98a0af;
  font-size: 12.5px;
}

.detail-empty i {
  color: #c6ccd7;
  font-size: 25px;
}

.detail-empty--chart {
  height: 100%;
  min-height: 180px;
  border: 1px dashed #e3e7ef;
  border-radius: 10px;
  background: #fbfcfe;
}

.detail-empty--compact {
  min-height: 74px;
}

.martin-tools,
.strategy-parameters,
.order-card {
  margin-top: 20px;
}

.martin-tools__body {
  justify-content: space-between;
  gap: 22px;
  padding: 2px 22px 22px;
}

.martin-shortcuts,
.martin-operations {
  display: flex;
  align-items: center;
  gap: 10px;
}

.martin-shortcuts button,
.martin-operations button {
  height: 36px;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
  color: #5a6072;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}

.martin-shortcuts button {
  display: inline-flex;
  min-width: 105px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 10px 0 13px;
}

.martin-shortcuts button i {
  position: relative;
  width: 28px;
  height: 16px;
  border-radius: 99px;
  background: #c7ccd6;
  transition: background .16s ease;
}

.martin-shortcuts button i::after {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  content: "";
  transition: transform .16s ease;
}

.martin-shortcuts button.is-active {
  border-color: #b8e6ca;
  color: #0b7a47;
  background: #f3fbf6;
}

.martin-shortcuts button.is-active i {
  background: #12b76a;
}

.martin-shortcuts button.is-active i::after {
  transform: translateX(12px);
}

.martin-operations button {
  padding: 0 16px;
}

.martin-operations button:hover,
.martin-shortcuts button:hover {
  border-color: #a9c4f7;
  color: #1f6bff;
}

.martin-operations button.is-danger {
  border-color: #f3d2cf;
  color: #e5484d;
}

.strategy-parameters__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  padding: 2px 22px 22px;
  gap: 10px;
}

.strategy-parameters__grid > div {
  min-width: 0;
  padding: 13px 15px;
  border: 1px solid #eef1f7;
  border-radius: 10px;
  background: #fbfcfe;
}

.strategy-parameters__grid dt {
  color: #8a90a0;
  font-size: 11.5px;
}

.strategy-parameters__grid dd {
  margin: 5px 0 0;
  overflow: hidden;
  color: #303544;
  font-size: 13.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.order-card {
  overflow: hidden;
}

.order-card__header {
  padding-bottom: 15px;
}

.order-card__filters {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: #f5f6f9;
}

.order-card__filters:empty {
  display: none;
}

.order-card__filters button {
  padding: 6px 13px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #7d8493;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.order-card__filters button.is-active {
  background: #fff;
  color: #1f6bff;
  box-shadow: 0 1px 4px rgba(16, 24, 40, .08);
}

.order-table {
  width: 100%;
  overflow-x: auto;
}

.order-table__row {
  display: grid;
  min-width: 1000px;
  grid-template-columns: 1.35fr .8fr 1fr 1fr 1fr .8fr;
  align-items: center;
  gap: 10px;
  padding: 13px 24px;
  border-top: 1px solid #f3f4f8;
  color: #5a6072;
  font-size: 13px;
}

.order-table__row.has-detail {
  grid-template-columns: 1.35fr .8fr 1fr 1fr 1fr .8fr .55fr;
}

.order-table__row:not(.order-table__head):hover {
  background: #fafbfe;
}

.order-table__head {
  padding-top: 9px;
  padding-bottom: 9px;
  color: #8a90a0;
  background: #fbfcfe;
  font-size: 12px;
  font-weight: 700;
}

.order-table__row > span,
.order-table__row > strong {
  min-width: 0;
  overflow: hidden;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.order-table__row > strong {
  color: #303544;
}

.status-tag {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  font-style: normal;
}

.status-tag.is-filled {
  color: #0b7a47;
  background: #e8f6ee;
}

.status-tag.is-pending {
  color: #9a6700;
  background: #fff6dc;
}

.status-tag.is-cancelled {
  color: #737a89;
  background: #f0f2f6;
}

.order-table__action {
  text-align: right;
}

.order-table__action button {
  padding: 4px 9px;
  border: 1px solid #d9e6fc;
  border-radius: 6px;
  background: #f3f7fe;
  color: #1f6bff;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
}

.detail-empty--table {
  border-top: 1px solid #f3f4f8;
}

.order-card__pagination {
  display: flex;
  justify-content: flex-end;
  padding: 16px 22px 19px;
  border-top: 1px solid #f3f4f8;
}

.detail-modal {
  position: fixed;
  z-index: 220;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(22, 28, 45, .32);
  backdrop-filter: blur(2px);
}

.detail-modal__card {
  position: relative;
  width: min(470px, calc(100vw - 40px));
  padding: 27px;
  border: 1px solid rgba(231, 234, 241, .94);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 22px 70px rgba(16, 24, 40, .2);
  animation: detail-modal-in .18s ease;
}

@keyframes detail-modal-in {
  from { opacity: 0; transform: translateY(8px) scale(.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.detail-modal__card--wide {
  width: min(760px, calc(100vw - 40px));
  max-height: calc(100vh - 60px);
  overflow: auto;
}

.detail-modal__close {
  position: absolute;
  top: 17px;
  right: 17px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: #f5f6f9;
  color: #737a89;
  cursor: pointer;
}

.detail-modal__icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #ebf1fe;
  color: #1f6bff;
  font-size: 19px;
}

.detail-modal__icon.is-danger {
  background: #fceae8;
  color: #e5484d;
}

.detail-modal__card h3 {
  margin: 16px 0 0;
  color: #1b1f2a;
  font-size: 18px;
  font-weight: 800;
}

.detail-modal__card > p {
  margin: 8px 0 0;
  color: #737a89;
  font-size: 13px;
  line-height: 1.65;
}

.replenishment-form {
  display: grid;
  margin-top: 19px;
  gap: 14px;
}

.replenishment-form label {
  display: grid;
  gap: 7px;
}

.replenishment-form label > span {
  color: #3a3f4e;
  font-size: 12.5px;
  font-weight: 700;
}

.replenishment-form input:not([type="checkbox"]) {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  outline: none;
  color: #1b1f2a;
  font-size: 13px;
}

.replenishment-form input:not([type="checkbox"]):focus {
  border-color: #8fb2f5;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, .09);
}

.replenishment-form small {
  color: #98a0af;
  font-size: 11px;
}

.replenishment-form__check {
  display: flex !important;
  align-items: center;
  grid-template-columns: auto 1fr;
}

.replenishment-form__check input {
  accent-color: #1f6bff;
}

.detail-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 23px;
}

.detail-modal__check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  color: #5a6072;
  font-size: 12.5px;
  cursor: pointer;
}

.detail-modal__check input {
  accent-color: #1f6bff;
}

.detail-modal__actions button {
  min-width: 92px;
  height: 38px;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
  color: #5a6072;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.detail-modal__actions button:last-child {
  border-color: #1f6bff;
  background: #1f6bff;
  color: #fff;
}

.detail-modal__actions button.is-danger {
  border-color: #e5484d;
  background: #e5484d;
}

.detail-modal__actions button:disabled {
  cursor: wait;
  opacity: .65;
}

.order-detail-list {
  display: grid;
  margin-top: 18px;
  gap: 10px;
}

.order-detail-list > div {
  display: grid;
  grid-template-columns: 72px 1fr;
  align-items: center;
  gap: 13px;
  padding: 13px;
  border: 1px solid #eef1f7;
  border-radius: 10px;
  background: #fbfcfe;
}

.order-detail-list dl {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  gap: 12px;
}

.order-detail-list dt {
  color: #8a90a0;
  font-size: 10.5px;
}

.order-detail-list dd {
  margin: 3px 0 0;
  overflow: hidden;
  color: #3a3f4e;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.detail-modal-enter-active,
.detail-modal-leave-active {
  transition: opacity .16s ease;
}

.detail-modal-enter,
.detail-modal-leave-to {
  opacity: 0;
}

@media (max-width: 1180px) {
  .strategy-metrics {
    grid-template-columns: repeat(3, 1fr);
  }

  .strategy-metric:nth-child(4) {
    border-left: 0;
    border-top: 1px solid #f0f2f6;
  }

  .strategy-metric:nth-child(5),
  .strategy-metric:nth-child(6) {
    border-top: 1px solid #f0f2f6;
  }

  .strategy-detail__main-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .strategy-detail__heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .strategy-detail__actions {
    width: 100%;
    overflow-x: auto;
  }

  .strategy-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .strategy-metric:nth-child(3),
  .strategy-metric:nth-child(5) {
    border-left: 0;
  }

  .strategy-metric:nth-child(3) {
    border-top: 1px solid #f0f2f6;
  }

  .strategy-detail__main-grid {
    display: block;
  }

  .position-card {
    margin-top: 16px;
  }

  .martin-tools__body {
    align-items: stretch;
    flex-direction: column;
  }

  .martin-shortcuts,
  .martin-operations {
    flex-wrap: wrap;
  }

  .strategy-parameters__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pending-order-card__grid {
    grid-template-columns: 1fr;
  }
}
</style>
